import { useState, useEffect } from "react";

// STYLES
import * as Style from "./UsersDetailsStyle";

// LIBRARIES
import utcPlugin from "dayjs/plugin/utc";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

// MISC
import { IProps, IModalData } from "./UsersDetailsModel";
import { COLORS } from "constants/styleVariables";
import {
  ButtonTypes,
  ChildModalTypes,
  ModalTypes,
  NewcomeUserStatus,
} from "models/GeneralTypes/generalTypes";
import { userTestData } from "../../../../../mocks/TestMessages";
import { IRentalContent, IUsersApiParams } from "models/interfaces";

// ASSETS
import PersonDefaultIcon from "assets/Icons/defaultPersonPhoto.jpg";

// REDUX
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import {
  toggleChildModalState,
  modalProps,
  toggleModalState,
  language,
} from "slices/uiSlice";
import { getUserRentals } from "api/rentalApi";
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import { addToBlackList, getAllUsers, removeFromBlacklist } from "api/userApi";
import { usersFilters } from "slices/userSlice";

// COMPONENTS

const UsersDetails = (props: IProps) => {
  // PROPS
  const { handleClose = () => {} } = props;

  // CONSTANTS USING LIBRARIES
  const dispatch = useAppDispatch();
  const modalPropsData = useAppSelector<IModalData>(modalProps);
  dayjs.extend(utcPlugin);
  const { t } = useTranslation();
  const selectedLanguage = useAppSelector<string>(language);
  const usersFiltersValue = useAppSelector<IUsersApiParams>(usersFilters);

  // CONSTANTS USING HOOKS
  const [userRentalsData, setUserRentalsData] = useState<IRentalContent[]>();
  const [statusColor, setStatusColor] = useState<string>("");
  const [userStatus, setUserStatus] = useState<string>("");

  // GENERAL CONSTANTS

  // USE EFFECT FUNCTION
  useEffect(() => {
    if (modalPropsData?.user?.userStatus === NewcomeUserStatus.VERIFIED) {
      setStatusColor(COLORS.primaryGreen);
    } else {
      setStatusColor(COLORS.terraCotta);
    }
    // eslint-disable-next-line
  }, [modalPropsData?.user?.userStatus, selectedLanguage]);

  useEffect(() => {
    dispatch(getUserRentals(modalPropsData?.user?.id))
      .unwrap()
      .then((response) => {
        setUserRentalsData(response);
      });
    //eslint-disable-next-line
  }, []);

  // REQUEST API FUNCTIONS

  // HANDLERS FUNCTIONS
  const handleOpenChildModal = (id) => {
    dispatch(
      toggleChildModalState({
        isVisible: true,
        type: ChildModalTypes.ID,
        content: id,
      })
    );
  };

  const handleNavigate = (rentalData) => {
    dispatch(
      toggleModalState({
        isVisible: true,
        type: ModalTypes.RENTAL,
        rental: rentalData,
        user: modalPropsData,
        rentalUser: true,
      })
    );
  };

  return (
    <Style.ModalContainer>
      <Style.TopContainer>
        <Style.CloseIconContainer>
          <Style.CloseIcon onClick={handleClose} />
        </Style.CloseIconContainer>
        <Style.TopContentWrapper>
          <Style.UserContainer>
            <Style.UserImageContainer
              src={modalPropsData?.user?.userPhoto || PersonDefaultIcon}
            />
            <Style.UserInfoTopContainer>
              <Style.UserNameContainer>
                {modalPropsData?.user?.username || ""}
              </Style.UserNameContainer>
              <Style.UserStatusContainer>
                <Style.Dot color={statusColor || ""} />
                <Style.StatusValue color={statusColor || ""}>
                  {userStatus === ""
                    ? t(`user.status.${modalPropsData?.user?.userStatus}`) || ""
                    : t(`user.status.${userStatus}`)}
                </Style.StatusValue>
              </Style.UserStatusContainer>
            </Style.UserInfoTopContainer>
          </Style.UserContainer>
          <Style.BlackListButtonsContainer>
            {userStatus !== NewcomeUserStatus.BLACKLIST && (
              <ButtonAtom
                text={t("modal.userDetails.button.addBlacklist")}
                type={ButtonTypes.THIRD_BUTTON}
                onClick={() =>
                  dispatch(addToBlackList(modalPropsData?.user?.id))
                    .unwrap()
                    .then((response) => {
                      setUserStatus(response.userStatus);
                      dispatch(getAllUsers(usersFiltersValue));
                    })
                }
              />
            )}
            {userStatus === NewcomeUserStatus.BLACKLIST && (
              <ButtonAtom
                text={t("modal.userDetails.button.removeFromBlacklist")}
                type={ButtonTypes.THIRD_BUTTON}
                onClick={() =>
                  dispatch(removeFromBlacklist(modalPropsData?.user?.id))
                    .unwrap()
                    .then((response) => {
                      setUserStatus(response.userStatus);
                      dispatch(getAllUsers(usersFiltersValue));
                    })
                }
              />
            )}
          </Style.BlackListButtonsContainer>
        </Style.TopContentWrapper>
      </Style.TopContainer>
      <Style.BottomContainer>
        <Style.UserInfoBottomContainer>
          <Style.UserContactInfoWrapper>
            <Style.BottomSubtitle>
              {t("modal.userDetails.contacts")}
            </Style.BottomSubtitle>
            <Style.UserInfoWrapper>
              <Style.UserInfoLine>
                <Style.IconPhone />
                <Style.UserInfo>
                  {modalPropsData?.user?.phoneNumber || ""}
                </Style.UserInfo>
              </Style.UserInfoLine>
            </Style.UserInfoWrapper>
          </Style.UserContactInfoWrapper>
          <Style.UserIdWrapper>
            <Style.BottomSubtitle>
              {t("modal.userDetails.id")}
            </Style.BottomSubtitle>
            <Style.UserIdImagesWrapper>
              <Style.UserIdImageContainer
                src={userTestData?.userId1 || ""}
                onClick={() =>
                  handleOpenChildModal(userTestData?.userId1 || "")
                }
              />
              <Style.UserIdImageContainer
                src={userTestData?.userId2 || ""}
                onClick={() =>
                  handleOpenChildModal(userTestData?.userId2 || "")
                }
              />
            </Style.UserIdImagesWrapper>
          </Style.UserIdWrapper>
        </Style.UserInfoBottomContainer>
        {/* <Style.UserDetailsActionsWrapper>
                <Style.SendMessageWrapper>
                    <Style.BottomSubtitle>
                        Trimite mesaj
                    </Style.BottomSubtitle>
                    <Style.SendMessageContainer>
                        <Style.SendMessageAdminIcon src={messagesTestData[0].userPhoto} alt="" />
                        <Style.InputAtomContainer>
                            <InputAtom
                                placeholder="Cauta..."
                                hasLabel={false}
                                inputValue={inputValue}
                                setInputValue={setInputValue}
                            />
                        </Style.InputAtomContainer>
                        <Style.SendMessageIcon src={SendMessageIcon || ""} />
                    </Style.SendMessageContainer>
                    <Style.MessagesContainer>
                        {messagesTestData.map((message) => (
                            <Style.MessageContainer>
                                <Style.MessageHeader>
                                    <Style.User>
                                        <Style.SendMessageAdminIcon src={message?.userPhoto || ""} />
                                        {message?.username || ""}
                                    </Style.User>
                                    <Style.Date>
                                        {message?.timeAgo || ""}
                                    </Style.Date>
                                </Style.MessageHeader>
                                <Style.MessageContent>
                                    {message?.userMessage || ""}
                                </Style.MessageContent>
                            </Style.MessageContainer>
                        ))}
                    </Style.MessagesContainer>
                </Style.SendMessageWrapper>
                <Style.SelectStatusWrapper>
                    <Style.BottomSubtitle>
                        Status
                    </Style.BottomSubtitle>
                    <Style.SelectInputAtomContainer>
                        <SelectAtom
                            hasLabel={false}
                            options={statusOptions}
                            selectedOptions={selectedStatusOption}
                            setSelectedOptions={setSelectedStatusOption}
                        />
                    </Style.SelectInputAtomContainer>
                </Style.SelectStatusWrapper>
            </Style.UserDetailsActionsWrapper> */}
      </Style.BottomContainer>
      <Style.UserRentalsContent>
        {userRentalsData?.length > 0 && (
          <Style.UserRentalsTitle>
            {t("modal.userDetails.rentals")}
          </Style.UserRentalsTitle>
        )}
        <Style.UserRentalsContainer>
          {userRentalsData &&
            userRentalsData.map((userRentalsDetails) => {
              return (
                <Style.UserRentalContainer
                  onClick={() => handleNavigate(userRentalsDetails)}
                >
                  <Style.UserLeftContent>
                    <Style.UserRentalId>
                      {t("modal.userDetails.rentalId")} {userRentalsDetails.id}
                    </Style.UserRentalId>
                    <Style.UserRentalStatus>
                      {t("modal.userDetails.rentalStatus")}{" "}
                      {userRentalsDetails.status}
                    </Style.UserRentalStatus>
                  </Style.UserLeftContent>
                  <Style.UserRightContent>
                    <Style.UserRentalTime>
                      {t("modal.userDetails.startTime")}{" "}
                      {dayjs(userRentalsDetails.startTime).format("HH:mm:ss")}
                    </Style.UserRentalTime>
                    <Style.UserRentalTime>
                      {t("modal.userDetails.endTime")}{" "}
                      {userRentalsDetails?.endTime != null
                        ? dayjs(userRentalsDetails?.endTime).format("HH:mm")
                        : "IN PROGRESS"}
                    </Style.UserRentalTime>
                    <Style.UserRentalTime>
                      {t("modal.userDetails.duration")}{" "}
                      {userRentalsDetails?.endTime != null
                        ? dayjs
                            .utc(
                              dayjs(userRentalsDetails.endTime).diff(
                                dayjs(userRentalsDetails.startTime),
                                "second"
                              ) * 1000
                            )
                            .format("HH:mm")
                        : ""}
                    </Style.UserRentalTime>
                  </Style.UserRightContent>
                </Style.UserRentalContainer>
              );
            })}
        </Style.UserRentalsContainer>
      </Style.UserRentalsContent>
    </Style.ModalContainer>
  );
};

export default UsersDetails;
